/**
 * useChatNavigation — scope-aware path builders for chat + roundtable.
 *
 * G9 (JAM-144): encapsulates the recurring `projectId ? "/projects/..."
 * : "/coaches/..."` branching that previously lived inline in
 * `PersonaCard`, `Sidebar`, `Chat`, and `Roundtable`. Those sites were
 * subtly different from each other — some had extra defensive fallbacks,
 * some didn't — and each new consumer had to re-derive the scope-aware
 * URL shape from scratch. One hook keeps the navigation contract in a
 * single place so future routes (or a rename of the project/coaches
 * prefixes) don't require a grep-and-fix sweep across call sites.
 *
 * The path builders are pure functions exported alongside the hook so
 * they can be tested without a React render — the hook simply
 * memoises them against `projectId`. Tests import the pure helpers.
 */

import { useMemo } from "react";

/**
 * Build the chat URL for a given scope.
 *
 * @param {string | null | undefined} projectId
 *   When truthy, the returned path is `/projects/{projectId}/chat/...`.
 *   When falsy (null/undefined/""), the path is `/coaches/chat/...`.
 * @param {string} personaId — required; the persona to chat with.
 * @param {string | null | undefined} conversationId
 *   Optional; when present, appended as the final path segment so the
 *   chat page hydrates an existing conversation.
 * @returns {string} absolute path — never a trailing slash.
 */
export function buildChatPath(projectId, personaId, conversationId) {
  const base = projectId
    ? `/projects/${projectId}/chat/${personaId}`
    : `/coaches/chat/${personaId}`;
  return conversationId ? `${base}/${conversationId}` : base;
}

/**
 * Build the roundtable URL for a given scope.
 *
 * @param {string | null | undefined} projectId
 *   When truthy, returns `/projects/{projectId}/roundtable[/{conversationId}]`.
 *   When falsy, returns `/coaches/roundtable[/{conversationId}]`.
 * @param {string | null | undefined} conversationId
 *   Optional; present for resume / deep-link flows, absent for a new
 *   roundtable where the page opens the PanelPicker.
 * @returns {string} absolute path — never a trailing slash.
 */
export function buildRoundtablePath(projectId, conversationId) {
  const base = projectId
    ? `/projects/${projectId}/roundtable`
    : "/coaches/roundtable";
  return conversationId ? `${base}/${conversationId}` : base;
}

/**
 * Build the scope-root URL — the parent of chat + roundtable surfaces.
 *
 * Use this for "back to list" navigations: the target after deleting
 * the active conversation, the `backTo` prop on Roundtable, or the
 * href for "new conversation" links that should land the user on the
 * scope's landing page.
 *
 * @param {string | null | undefined} projectId
 *   When truthy, returns `/projects/{projectId}`.
 *   When falsy, returns `/coaches`.
 * @returns {string} absolute path — never a trailing slash.
 */
export function buildScopeRoot(projectId) {
  return projectId ? `/projects/${projectId}` : "/coaches";
}

/**
 * Hook wrapping the path builders. Returns memoised closures so
 * consumers can pass the builders through to child components without
 * busting reference equality on every render. `scopeRoot` is a string
 * (not a closure) because it has no per-call parameters — it's the
 * parent URL of the scope itself.
 */
export function useChatNavigation(projectId) {
  return useMemo(
    () => ({
      chatPath: (personaId, conversationId = null) =>
        buildChatPath(projectId, personaId, conversationId),
      roundtablePath: (conversationId = null) =>
        buildRoundtablePath(projectId, conversationId),
      scopeRoot: buildScopeRoot(projectId),
    }),
    [projectId],
  );
}
